import * as actions_admin from 'actions/admin-actions';
import * as actions_attachments from 'actions/attachments-actions';
import * as toolbox from 'components/common/toolbox';
import AttachementGallery from 'components/attachments/attachment-gallery';
import AttachmentForm from 'components/attachments/attachment-form/attachment-form';
import Authorize from 'components/common/authorize';
import CatchErrors from 'components/common/catch-errors';
import React, { useEffect, useState } from 'react';
import _ from 'lodash';
import { Button, ButtonGroup } from 'react-bootstrap';
import { Ibox } from 'enspire-manager-framework';
import { PERMISSIONS } from 'components/constants';
import { Route, useHistory, useLocation, useParams, useRouteMatch } from 'react-router-dom';
import { Table } from 'em-table';
import { imageSize } from 'components/common/toolbox';
import { useDispatch, useSelector } from 'react-redux'

const AttachmentsTab = (props) => {

    // CANNOT HAVE TWO ATTACHMENT-TABS ACTIVE AT THE SAME TIME OR REFRESH LIMIT WILL OCCUR

    // props.id - id for attachment doc
    // props.source - path to doc e.g. 'assets' to read/write '[path]/[path]/[id]' 
    // props.prefix - prefix for Action Type e.g. [PREFIX]_ATTACHMENTS
    // props.field - for changing from default "attachments"
    // props.store & props.singular for accessing store e.g. useSelector((store) => [store].[singular]);

	const seg = 2;
    const history = useHistory();
    const dispatch = useDispatch();
    const params = useParams();
	const routeLocation = useLocation();
    const pathname = routeLocation.pathname;

	const admin = useSelector((store) => store.admin);
    const table = useSelector((store) => store[props.store]);
    
    const field = (props.field) ? props.field : 'attachments';
    const attachments = table[props.singular + '_' + field] ?? [];

    const [ data, setData ] = useState([]);
    const [ galleryPhotos, setGalleryPhotos ] = useState([]);
    const [ tabView, setTabView ] = useState('list');

    // Permissions ----------------------------------------

    const canView = props.permissions?.canView;
    const canEdit = props.permissions?.canEdit;

    /* Effects -----------------------------------------------------------------------------------------------------------------------*/
    
    useEffect(() => {
		dispatch(actions_attachments.subAttachments(params.handle, props.id, props.source, props.prefix, field));
		return () => {
			const unsubscribe = table[`${props.singular}_${field}_unsubscribe`];
			if (typeof unsubscribe == 'function') unsubscribe();
		};
	}, [params.handle, props.id, props.source]);

	useEffect(() => {
        var newPhotos = [];
        attachments.forEach((attach, i) => {
            if (attach.images) attach.images.forEach((image) => {
                newPhotos.push({ name: attach.name, ...image });
            });
        });
        const result = Object.groupBy(newPhotos, ({ name }) => name);
        setGalleryPhotos(result);

	}, [attachments]);

	useEffect(() => {
        var newData = [];       
        attachments.forEach((attach, i) => {
            newData.push({ ...attach, index: i, images: (attach.images?.length) ? <i className="fa-solid fa-camera fa-xl" role="button" onClick={ () => toggleLightbox(i) }></i> : null });
        });

        setData(newData)
	}, [attachments]);

	// Trigger lightbox when URL param changes ------------------------------------------------------------------

    useEffect(() => {
        const [ attachIndex, imageIndex] = params.form_id?.split(':').map((i) => parseInt(i)) || [];
        const attach = attachments[attachIndex];

        if (params.form == 'lightbox' && params.form_id && attach) {

            const images = attach.images?.map((image) => image.imageUrl.includes('.pdf')
                ?   { type: 'custom', content: toolbox.getEmbedCode(image.imageUrl), title: attach.name, description: image.caption }
                :   { src: imageSize(image.imageUrl, 'lg'), title: attach.name, description: image.caption }
            );
            dispatch(actions_admin.lightboxSources([ ...images ]));
            dispatch(actions_admin.lightboxIndex(imageIndex));
            dispatch(actions_admin.lightboxShow(true));
        } else if (params.form == 'form'){
            dispatch(actions_admin.lightboxShow(false));
        }
	}, [params.form, params.form_id]);

    /* Handlers --------------------------*/

    const handleAddNew = () => {
		let location = toolbox.modifyPath(pathname, seg+3, "attachment-form/0");
		history.push({ pathname: location });
    };

    /* Actions --------------------------*/
    
	const openForm = (item) => {
        let location = toolbox.modifyPath(pathname, seg+3, `attachment-form/${item.name}`);
        history.push({ pathname: location });
	};

	const toggleLightbox = (attachIndex, imageIndex) => {
        let location = toolbox.modifyPath(pathname, seg+3, `lightbox/${attachIndex}:${(imageIndex ?? 0)}`);
		history.push({ pathname: location });
	}

    /* Constants --------------------------*/

    const columns = [
        { name: 'Name', field: 'name', width: 80},
        { name: 'Image', field: 'images', type: 'jsx', width: 10 },
        { name: '', field: 'id', type: 'button', button: { name: <i className="fa-regular fa-gear fa-lg text-muted"></i>, className: 'btn-default btn-xs float-right', callback: openForm }, width: 10 }
    ];

    var galleries = [];
    let index = 0;
    for (let name of Object.keys(galleryPhotos)) {
        galleries.push(<AttachementGallery name={ name } photos={ galleryPhotos[name] } toggleLightbox={ toggleLightbox } attachIndex={ index++ } />);
    }
   
    return (
        <>
            <Authorize perm={ PERMISSIONS.WORK_ORDERS_VIEW } or={ canView } show403>
                <Ibox 
                    title={'Attachments'}
                    button={(props.button) ? props.button : '+ Attachment'}
                    button_callback={handleAddNew}
                    button_disabled={ !canEdit }
                >
                    <ButtonGroup className="mb-3">
                        <Button variant={ (tabView == 'list') ? 'secondary' : 'default' } onClick={ () => setTabView('list') }>List View</Button>
                        <Button variant={ (tabView == 'gallery') ? 'secondary' : 'default' } onClick={ () => setTabView('gallery') }>Gallery View</Button>
                        { props.store == 'serviceRequests' &&
                            <Button variant={ (tabView == 'all') ? 'secondary' : 'default' } onClick={ () => setTabView('all') }>Work Orders Gallery</Button>
                        }
                    </ButtonGroup>
                    { tabView == 'list'
                        ?   <>
                                <h3>{ `${props.name} Attachments` }</h3>
                                <Table id="attachments-tab"
                                    data={ data }
                                    columns={columns}
                                    on_focus={(target) => props.setFocused(target)}
                                    style={{ minHeight: '330px' }}
                                />
                            </>
                        :   galleries
                    }

                    { admin.flutter_keyboard &&
                        <div style={{ paddingBottom: '55vh' }}></div>
                    }
                </Ibox>
            </Authorize>

            <Route path={ "/:handle/:appId/:entity/:asset_id?/attachment-form/:attachment_name/:tab?"} render={(route_props) => (
                <Authorize perm={ PERMISSIONS.WORK_ORDERS_VIEW } or={ canView } show403>
                    <CatchErrors modal={true}>
                        <AttachmentForm {...route_props}
                            expires={ props.expires }
                            field={ props.field } 
                            id={ props.id } 
                            invoice={ props.invoice }
                            permissions={{ canView, canEdit }}
                            prefix={ props.prefix } 
                            singular={ props.singular } 
                            source={ props.source } 
                            store={ props.store } 
                        />
                    </CatchErrors>
                </Authorize>
            )} />

        </>
    );
};

export default AttachmentsTab;